import { create } from 'zustand';
import { getTenantId } from './authStore';

const INSIGHT_API = import.meta.env.VITE_INSIGHT_ENGINE_URL || 'http://localhost:8029';

export type InsightStatus = 'new' | 'seen' | 'pinned' | 'dismissed' | 'promoted' | 'aged';

export interface Insight {
  id: string;
  tenant_id: string;
  run_id: string;
  family: string;
  object_type_id: string;
  outcome_object_type_id: string | null;
  feature: Record<string, unknown>;
  outcome: Record<string, unknown>;
  n: number;
  effect_size: number;
  effect_metric: string;
  p_value: number | null;
  p_adjusted: number | null;
  direction: string | null;
  stability_score: number | null;
  replication_holdout_pass: boolean | null;
  causal_estimate: Record<string, unknown> | null;
  rank_score: number;
  novelty_score: number | null;
  prior_insight_id: string | null;
  title: string;
  // Readable title in the tenant's language, both languages, and how the
  // finding was found. Absent on runs written before them: use insightTitle().
  title_display?: string | null;
  title_i18n?: Record<string, string | null> | null;
  method?: string | null;
  description: string;
  recommendation: string | null;
  evidence: Record<string, unknown>;
  status: InsightStatus;
  discovered_at: string;
}

export interface InsightRun {
  id: string;
  tenant_id: string;
  started_at: string;
  finished_at: string | null;
  status: string;
  tests_planned: number | null;
  tests_run: number | null;
  insights_kept: number | null;
  families_run: string[] | null;
  family_durations_ms: Record<string, number> | null;
  duration_ms: number | null;
  peak_memory_mb: number | null;
  error: string | null;
  // A run stopped by a cap closes `ok` with what it computed; these say which
  // cap stopped it and how far it got.
  truncated?: boolean | null;
  truncation?: InsightRunTruncation | null;
}

export interface InsightRunTruncation {
  cap: string;
  caps?: string[];
  stage?: string;
  tests_run: number;
  tests_planned?: number;
  tests_total: number;
  families_pending?: string[];
  families_cut?: string[];
  message?: string;
}

export interface InsightConfig {
  tenant_id: string;
  enabled: boolean;
  schedule_cron: string;
  timezone: string;
  family_enabled: Record<string, boolean>;
  family_priors: Record<string, number>;
  max_tests: number;
  max_runtime_minutes: number;
  max_memory_mb: number;
  min_effect_size: number;
  min_sample_size: number;
  min_stability_score: number;
  feature_denylist: string[];
  outcome_denylist: string[];
  bootstrap_iterations: number;
  holdout_pct: number;
  keep_top_n: number;
  // How keep_top_n is spent: a fair share per family (default) or the top N by rank.
  keep_policy?: 'fair_share' | 'global_rank';
  // Language of the finding titles.
  language?: 'es' | 'en';
  llm_titles_enabled: boolean;
  embeddings_enabled: boolean;
  causal_enabled: boolean;
  cross_ot_enabled: boolean;
}

interface InsightState {
  insights: Insight[];
  selected: Insight | null;
  runs: InsightRun[];
  config: InsightConfig | null;
  loading: boolean;
  unreadCount: number;

  fetchInsights: (filter?: { status?: string; family?: string; object_type_id?: string; limit?: number }) => Promise<void>;
  fetchInsight: (id: string) => Promise<void>;
  patchStatus: (id: string, status: InsightStatus) => Promise<void>;
  /** `error` is the service's own detail when it refuses (409 the rule
   *  exists, 403 no permission, 502 alert service down), ready to show. */
  promoteToAlert: (id: string, threshold?: number) => Promise<{ ok: boolean; rule?: unknown; error?: string }>;
  investigate: (id: string) => Promise<{ module: string; object_type_id: string; filters: Record<string, unknown>; highlight_record_ids: string[] } | null>;

  fetchRuns: () => Promise<void>;
  /** Start a run, or attach to the one already in progress: the id of the run
   *  to follow either way (null when neither could be had). `objectTypeIds`
   *  (none or empty: every type) limits a new run to those object types. */
  runNow: (objectTypeIds?: string[]) => Promise<string | null>;
  /** Follow a run until it reaches a terminal status (or `signal` aborts),
   *  refreshing the run list on the way. */
  waitForRun: (runId: string, opts?: { intervalMs?: number; signal?: AbortSignal }) => Promise<InsightRun | null>;

  fetchConfig: () => Promise<void>;
  patchConfig: (patch: Partial<InsightConfig>) => Promise<void>;

  pollUnread: () => Promise<void>;
}

// How often a run started (or attached to) from the UI is checked.
export const RUN_POLL_MS = 5000;

export const isActiveRunStatus = (status?: string | null) => status === 'running' || status === 'queued';

const sleep = (ms: number, signal?: AbortSignal) => new Promise<void>(resolve => {
  if (signal?.aborted) { resolve(); return; }
  const id = setTimeout(() => { signal?.removeEventListener('abort', onAbort); resolve(); }, ms);
  function onAbort() { clearTimeout(id); resolve(); }
  signal?.addEventListener('abort', onAbort, { once: true });
});

function qs(filter: Record<string, unknown> | undefined) {
  const p = new URLSearchParams();
  p.set('tenant_id', getTenantId());
  if (filter) {
    Object.entries(filter).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
    });
  }
  return `?${p.toString()}`;
}

export const useInsightStore = create<InsightState>((set, get) => ({
  insights: [],
  selected: null,
  runs: [],
  config: null,
  loading: false,
  unreadCount: 0,

  fetchInsights: async (filter) => {
    set({ loading: true });
    try {
      const res = await fetch(`${INSIGHT_API}/insights${qs(filter)}`);
      const data = await res.json();
      set({ insights: data.insights || [] });
    } catch {
      set({ insights: [] });
    } finally {
      set({ loading: false });
    }
  },

  fetchInsight: async (id) => {
    try {
      const res = await fetch(`${INSIGHT_API}/insights/${id}${qs(undefined)}`);
      if (!res.ok) return;
      const data = await res.json();
      set({ selected: data });
    } catch {
      /* ignore */
    }
  },

  patchStatus: async (id, status) => {
    await fetch(`${INSIGHT_API}/insights/${id}${qs(undefined)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    set(state => ({
      insights: state.insights.map(i => i.id === id ? { ...i, status } : i),
      selected: state.selected && state.selected.id === id ? { ...state.selected, status } : state.selected,
    }));
  },

  promoteToAlert: async (id, threshold = 0.3) => {
    let res: Response;
    try {
      res = await fetch(`${INSIGHT_API}/insights/${id}/promote-to-alert${qs(undefined)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ threshold }),
      });
    } catch {
      return { ok: false };
    }
    if (!res.ok) {
      let error: string | undefined;
      try {
        const detail = (await res.json())?.detail;
        if (typeof detail === 'string') error = detail;
        else if (detail && typeof detail === 'object' && typeof detail.message === 'string') error = detail.message;
      } catch { /* not JSON: the caller shows its own message */ }
      return { ok: false, error };
    }
    const data = await res.json();
    set(state => ({
      insights: state.insights.map(i => i.id === id ? { ...i, status: 'promoted' } : i),
      selected: state.selected && state.selected.id === id ? { ...state.selected, status: 'promoted' } : state.selected,
    }));
    return { ok: true, rule: data.rule };
  },

  investigate: async (id) => {
    const res = await fetch(`${INSIGHT_API}/insights/${id}/investigate${qs(undefined)}`, {
      method: 'POST',
    });
    if (!res.ok) return null;
    return await res.json();
  },

  fetchRuns: async () => {
    try {
      const res = await fetch(`${INSIGHT_API}/insights/runs${qs(undefined)}`);
      const data = await res.json();
      set({ runs: data.runs || [] });
    } catch {
      set({ runs: [] });
    }
  },

  runNow: async (objectTypeIds) => {
    try {
      const scoped = !!objectTypeIds && objectTypeIds.length > 0;
      const res = await fetch(`${INSIGHT_API}/insights/runs/run-now${qs(undefined)}`, {
        method: 'POST',
        ...(scoped ? {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ object_type_ids: objectTypeIds }),
        } : {}),
      });
      // One run per tenant: while one is running or queued the service answers
      // 409 {detail: {error: 'run_in_progress', run_id}}. That run is the one
      // the caller asked for (a double click, a retry after a network blip),
      // so it is followed like a fresh start instead of being dropped.
      if (res.status === 409) {
        const body = await res.json().catch(() => null);
        const d = body?.detail && typeof body.detail === 'object' ? body.detail : body;
        return typeof d?.run_id === 'string' && d.run_id ? d.run_id : null;
      }
      if (!res.ok) return null;
      const data = await res.json();
      return data.queued_run_id || null;
    } catch {
      return null;
    }
  },

  waitForRun: async (runId, opts) => {
    const intervalMs = opts?.intervalMs ?? RUN_POLL_MS;
    const signal = opts?.signal;
    // No attempt cap: a capped run takes 15 min or more, and the service
    // closes every run (a stale one is marked failed), so this ends.
    while (!signal?.aborted) {
      await get().fetchRuns();
      const latest = get().runs.find(r => r.id === runId);
      if (latest && !isActiveRunStatus(latest.status)) return latest;
      await sleep(intervalMs, signal);
    }
    return null;
  },

  fetchConfig: async () => {
    try {
      const res = await fetch(`${INSIGHT_API}/insights/config${qs(undefined)}`);
      const data = await res.json();
      set({ config: data });
    } catch {
      /* ignore */
    }
  },

  patchConfig: async (patch) => {
    await fetch(`${INSIGHT_API}/insights/config${qs(undefined)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    await get().fetchConfig();
  },

  pollUnread: async () => {
    try {
      const res = await fetch(`${INSIGHT_API}/insights${qs({ status: 'new', limit: 200 })}`);
      const data = await res.json();
      set({ unreadCount: (data.insights || []).length });
    } catch {
      /* ignore */
    }
  },
}));
